import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wallet, Lock, Eye, EyeOff, Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (pw !== pw2) {
      toast.error("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Senha redefinida! Entre com a nova senha.");
      navigate("/entrar", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível redefinir a senha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md animate-slide-up">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
            <Wallet className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-white">Põe na Conta</p>
            <p className="text-xs text-emerald-300">Finanças por voz</p>
          </div>
        </div>

        <div className="pnc-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-24 -right-16 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
          <div className="relative">
            {!token ? (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-500/15 flex items-center justify-center mb-4">
                  <AlertCircle className="w-7 h-7 text-amber-300" />
                </div>
                <h1 className="font-display text-2xl font-bold text-white">Link inválido</h1>
                <p className="text-sm text-slate-400 mt-3">
                  Este link de redefinição não é válido. Solicite um novo e-mail de recuperação.
                </p>
                <Link
                  to="/esqueci-senha"
                  className="mt-5 inline-block btn-emerald py-3 px-6 rounded-2xl text-sm"
                >
                  Solicitar novo link
                </Link>
              </div>
            ) : (
              <>
                <h1 className="font-display text-2xl font-bold text-white">Nova senha</h1>
                <p className="text-sm text-slate-400 mt-2">
                  Crie uma senha segura com pelo menos 6 caracteres.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Nova senha</span>
                    <div className="mt-1.5 flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-slate-900/70 border border-white/10 focus-within:border-emerald-400/60 focus-within:bg-slate-900 transition-colors">
                      <Lock className="w-4 h-4 text-slate-500 shrink-0" />
                      <input
                        data-testid="reset-password-input"
                        type={showPw ? "text" : "password"}
                        required
                        minLength={6}
                        autoFocus
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm"
                      />
                      <button
                        type="button"
                        aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                        onClick={() => setShowPw((v) => !v)}
                        className="text-slate-400 hover:text-white p-1"
                      >
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Confirmar nova senha</span>
                    <div className="mt-1.5 flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-slate-900/70 border border-white/10 focus-within:border-emerald-400/60 focus-within:bg-slate-900 transition-colors">
                      <Lock className="w-4 h-4 text-slate-500 shrink-0" />
                      <input
                        data-testid="reset-password-confirm-input"
                        type={showPw ? "text" : "password"}
                        required
                        minLength={6}
                        value={pw2}
                        onChange={(e) => setPw2(e.target.value)}
                        placeholder="Repita a nova senha"
                        className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm"
                      />
                    </div>
                  </label>

                  <button
                    data-testid="reset-password-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-emerald w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {loading ? "Redefinindo…" : "Redefinir senha"}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/entrar"
                    className="text-xs text-slate-400 hover:text-white underline underline-offset-4"
                  >
                    Lembrou da senha? Entrar
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2026 Põe na Conta • suporte@poenaconta.ia.br
        </p>
      </div>
    </div>
  );
}
